import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { motion, useAnimation } from "framer-motion";
import { FaStar, FaRegCopy } from "react-icons/fa";
import StarView from "../global/StarView";
import { trackCasinoInsight } from "../../../controllers/InsightsController";
import { useNavigate } from "react-router-dom";

const chipClass =
  "inline-flex items-center rounded-full  dark:border-none  dark:bg-gray-800 border border-border/60 bg-surface px-3 py-1 text-[12px] leading-none text-text font-semibold shadow-[0_1px_0_rgba(0,0,0,0.03)]";

const CasinoCard = ({
  casino,
  index = 0,
  isOpen = false,
  onToggle = () => {},
  onDetailsClick = () => {},
}) => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const claimPulse = useAnimation();

  const rating = useMemo(() => {
    const n =
      typeof casino?.Rating === "number"
        ? casino.Rating
        : parseFloat(casino?.Rating || 0);
    return isNaN(n) ? 0 : Math.max(0, Math.min(5, n));
  }, [casino?.Rating]);
  const ratingNumber = useMemo(() => rating.toFixed(1), [rating]);

  const tags = useMemo(() => {
    if (Array.isArray(casino?.GoodFor) && casino.GoodFor.length) {
      return casino.GoodFor.slice(0, 4);
    }
    if (casino?.BestFor && typeof casino.BestFor === "string") {
      return casino.BestFor.split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 4);
    }
    return ["Withdrawal Time", "Game Selection", "Customer Service"].slice(
      0,
      3
    );
  }, [casino?.GoodFor, casino?.BestFor]);

  const logoSrc =
    casino?.ImageUrl || `${process.env.PUBLIC_URL}/assets/casino.png`;

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!casino?.promoCode) return;
    try {
      await navigator.clipboard.writeText(casino.promoCode);
      setCopied(true);
      claimPulse.start({
        scale: [1, 1.05, 1],
        transition: { duration: 0.8, repeat: 2, ease: "easeInOut" },
      });
      setTimeout(() => setCopied(false), 900);
    } catch {}
  };

  const handleClaim = (e) => {
    e.stopPropagation();
    if (casino?.CasinoUrl) {
      window.open(casino.CasinoUrl, "_blank", "noopener,noreferrer");
      trackCasinoInsight({
        casinoId: casino.id,
        type: "click",
        page: "Featured Casino Page",
      });
    }
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (!casino?.CasinoUrl) return;
    window.open(casino.CasinoUrl, "_blank", "noopener,noreferrer");
    trackCasinoInsight({
      casinoId: casino.id,
      type: "click",
      page: "Featured Casino Page",
    });
  };

  const toggleCard = useCallback(() => onToggle(index), [index, onToggle]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className="relative dark:bg-darkbg  text-black dark:text-white bg-white overflow-visible rounded-xl  bg-surface shadow-sm p-3 sm:p-4 mb-6"
      onClick={toggleCard}
      role="button"
      tabIndex={0}
    >
      {/* Promo pill (lifted) */}
      {!isOpen && casino?.promoCode && casino.promoCode !== "N/A" && (
        <div
          className="absolute text-promobrown bg-promoyellowlight rounded-md border border-promoyellowdark left-3 -top-1 -translate-y-1/2 transform z-20 text-[11px]  bg-surface/95 backdrop-blur px-2 py-1 shadow-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <strong>PROMO:</strong>
          {"  "}
          <span className="font-mono py-[2px] px-2 rounded-full bg-promoyellowdark">
            {casino.promoCode}
          </span>
        </div>
      )}

      {/* Main row */}
      <div className="grid grid-cols-1 md:grid-cols-4 items-start gap-4">
        {/* Left: logo + name/rating/review */}
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={handleImageClick}
            className="shrink-0 !w-[180px] rounded-xl dark:bg-gray-500/30  px-2 overflow-hidden 
             flex items-center justify-center 
             dark:shadow-[0_0_15px_3px_rgba(156,163,175,0.3)]          /* gray-400 @ 30% *"
            aria-label={`${casino?.Name || "Casino"} logo`}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <img
              src={logoSrc}
              alt={casino?.Name || "Casino"}
              className="w-[100%]   !object-contain"
            />
          </button>

          <div className="min-w-0 ml-2 flex flex-col items-start">
            <div className="text-base font-semibold leading-tight truncate">
              {casino?.Name || "Unknown Casino"}
            </div>

            <div className="mt-2 inline-flex items-center gap-1.5 h-7 rounded-md  dark:bg-gray-800 dark:border-none border border-border bg-surface px-2.5 text-[13px] leading-none">
              <FaStar className="h-3.5 w-3.5 text-amber-500" />
              <span className="font-semibold">{ratingNumber}</span>
              <span className="opacity-80">/5</span>
            </div>

            <div className="mt-2 ">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!casino?.Name) return;
                  const slug = casino.Name.toLowerCase().replace(/\s+/g, "-");
                  navigate(`/${slug}-review`, { state: { casino } });
                }}
                className="dark:border-none  dark:bg-gray-800 inline-flex text-nowrap items-center rounded-lg border border-border bg-surface px-3.5 py-1.5 text-sm font-medium text-text shadow-[0_1px_0_rgba(0,0,0,0.03)] hover:bg-primary/10 transition"
              >
                Review
              </button>
            </div>
          </div>
        </div>

        {/* Good for */}
        <div className="w-full">
          <div className="text-[12px] text-text/70 mb-1 text-left">
            Good for
          </div>
          <div className="flex flex-wrap gap-2 items-start ">
            {tags.map((t, i) => (
              <span key={`${t}-${i}`} className={chipClass}>
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Sign up offer */}
        <div className="text-left md:pr-3">
          <div className="text-[12px] text-text/70 mb-1">Sign up offer</div>
          <div className="text-[16px] sm:text-[18px] font-semibold leading-snug">
            {casino?.WelcomeOffer
              ? casino.WelcomeOffer
              : "Get 500 Free Spins with our Exclusive Bonus"}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-2">
          <motion.button
            animate={claimPulse}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleClaim}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-full inline-flex items-center justify-center h-10 px-4 rounded-lg bg-primary text-white font-semibold shadow-sm hover:bg-primary-hover transition border-0"
          >
            <span>Claim Bonus</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="ml-2 h-4 w-4 -mt-px"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="1.75"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 17L17 7M9 7h8v8"
              />
            </svg>
          </motion.button>
          {casino?.promoCode && casino.promoCode !== "N/A" && !isOpen && (
            <div className="relative " onMouseDown={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={handleCopy}
                className="w-full inline-flex items-center justify-center text-promobrown bg-promoyellowlight rounded-md border border-promoyellowdark gap-2 h-10   border-border bg-surface px-3 text-sm font-medium text-text  transition"
                title="Copy promo code"
              >
                <span className="bg-promoyellowdark rounded-full py-1 px-4">
                  {casino?.promoCode || "N/A"}
                </span>
                <FaRegCopy className="opacity-80" />
                {copied && !isOpen && (
                  <span className="absolute bottom-2 right-4 text-[11px] px-2 py-0.5 rounded bg-text dark:border-none  dark:bg-gray-800 text-black dark:text-white">
                    Copied
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ✅ Updated Show more button */}
      {!isOpen && (
        <div className="absolute  dark:bg-gray-800 shadow bg-white rounded-full right-3 -bottom-3 z-20">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(index);
            }}
            className="text-[12px] text-text dark:text-white rounded-full    bg-transparent px-3 py-1 hover:border-gray-300 dark:hover:border-gray-200 transition-colors duration-200 backdrop-blur-[2px]"
          >
            Show more +
          </button>
        </div>
      )}

      {/* Expanded details */}
      {isOpen && (
        <div className="mt-4" onClick={(e) => e.stopPropagation()}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium mb-2 text-left">
                Payment Methods
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {casino?.PaymentMethods?.length ? (
                  casino.PaymentMethods.map((m, i) => (
                    <img
                      key={`${m.name}-${i}`}
                      src={m.imageUrl}
                      alt={m.name}
                      className="dark:border-none  dark:bg-gray-800  h-6 w-auto rounded-md border  border-border bg-surface p-0.5"
                    />
                  ))
                ) : (
                  <p className="text-sm text-text/70">
                    No payment methods available
                  </p>
                )}
              </div>
            </div>

            <div>
              {casino?.promoCode && casino.promoCode !== "N/A" && (
                <div className="text-promobrown bg-promoyellowlight rounded-md border border-promoyellowdark  flex items-center justify-between  border-border bg-surface px-3 py-2">
                  <div className="text-sm">
                    <strong>PROMO CODE:</strong>{" "}
                    <span className="font-mono relative bg-promoyellowdark rounded-full py-1 px-4">
                      {casino.promoCode}
                    </span>
                  </div>
                  <button
                    className="ml-3 relative text-promobrown bg-promoyellowlight rounded-md border border-promoyellowdark  inline-flex items-center gap-2  border-border bg-surface px-2 py-1 text-sm text-text hover:bg-primary/5 transition"
                    onClick={handleCopy}
                  >
                    Copy <FaRegCopy />
                    {copied && isOpen && (
                      <span className="absolute bottom-0 right-20 text-[11px] px-2 py-0.5 rounded bg-text text-black dark:text-white">
                        Copied
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="dark:border-none  dark:bg-gray-800  rounded-lg border border-border bg-surface p-3 text-sm">
              <strong>Minimum Deposit:</strong> {casino?.MinDeposit || "N/A"}
            </div>
            <div className="dark:border-none  dark:bg-gray-800 rounded-lg border border-border bg-surface p-3 text-sm">
              <strong>Payout Speed:</strong> {casino?.PayoutSpeed || "N/A"}
            </div>
            <div className="dark:border-none  dark:bg-gray-800 rounded-lg border border-border bg-surface p-3 text-sm">
              <strong>RTP:</strong> {casino?.Rtp || "N/A"}
            </div>
            <div className="dark:border-none  dark:bg-gray-800 rounded-lg border border-border bg-surface p-3 text-sm">
              <div className="flex items-center gap-2">
                {StarView(casino?.Rating || 0)}
                <span className="text-sm font-medium">{ratingNumber}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

CasinoCard.propTypes = {
  casino: PropTypes.object.isRequired,
  index: PropTypes.number,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  onDetailsClick: PropTypes.func,
};

export default React.memo(CasinoCard);
